import React, { useCallback, useMemo, useRef } from 'react';
import * as echarts from 'echarts';
import 'echarts-extension-gmap';

import _ from 'lodash';
import ReactEcharts from 'echarts-for-react';
import {
    DEFAULT_MAP_ZOOM,
    UNKNOWN_DATUM,
    ZERO_MARK_FRANCE_LAT,
    ZERO_MARK_FRANCE_LON,
} from 'business/constants';
import { capitalize, capitalizeAndKeepTheRest } from 'helpers/helpers';
import { formatDisplay, flattenPolygonData } from 'helpers/formatHelper';
import { getContrastedColors } from 'helpers/eChartHelpers';
import { ChartSettingsT } from 'business/definitions';
import { getLegendWidth } from 'helpers/chart/legendHelpers';
import { PointT, SerieT, TwoDimGraphProps } from '../types';
import { getColorsScale } from './helpers/getColor';
import Loading from 'components/Loading';
import { useLoadGoogleMaps } from 'components/ChartContainer/components/Chart/graphs/hooks/loadGoogleMaps';
import {
    EChartGmapOptions,
    getGmapOptionsFromCoordinate,
    getGmapOptionsFromPolygon,
} from './helpers/mapHelpers';

const concatDataPointCoordinates = (
    d: Record<string, unknown>,
    xKey: string,
    yKey: string,
): string[] => {
    const xValue = d[xKey];
    const yValue = d[yKey];
    if (typeof xValue !== 'string') {
        throw new Error(`xValue should be a string, but is ${typeof xValue}`);
    }
    return xValue.split(' - ').concat(`${yValue}`);
};

const setMapSettings = (
    settings: ChartSettingsT,
    echartRef:
        | (echarts.EChartsCoreOption & EChartGmapOptions)
        | null
        | undefined,
    applyChartSettings: ((a: ChartSettingsT) => void) | undefined,
) => {
    if (applyChartSettings) {
        // @ts-expect-error [TS migration] (previously $FlowFixMe)
        const echartsInstance = echartRef.getEchartsInstance();
        const mapOptions = echartsInstance.getOption().gmap[0];

        const { mapZoom, mapLatCenter, mapLonCenter } = settings;
        const hasSettingsChanged =
            mapZoom !== mapOptions.zoom ||
            mapLonCenter !== mapOptions.center[0] ||
            mapLatCenter !== mapOptions.center[1];

        if (hasSettingsChanged) {
            applyChartSettings({
                ...settings,
                mapZoom: mapOptions.zoom,
                mapLatCenter: mapOptions.center[1],
                mapLonCenter: mapOptions.center[0],
            });
        }
    }
};

// The GMap extension for EChart can use more type than just PointT[]
type GMapData =
    | PointT
    | {
          value: string[];
      }
    | { value: string[]; name: string };

// In the same way, the extension has extra data in the serie type.
type GMapSerieT = Omit<SerieT, 'data'> & {
    data: GMapData[];
    coordinateSystem: string;
    renderItem?: (
        params: echarts.CustomSeriesRenderItemParams,
        api: echarts.CustomSeriesRenderItemAPI,
    ) => {
        type: string;
        style: object;
        shape: { points: number[][] };
    };
    itemStyle?: object;
    symbolSize(val: number[]): number;
};

const MapChart = ({
    height,
    width,
    data,
    orderedColumns,
    settings,
    applyChartSettings,
    isChartViewedOnDashboard = false,
}: TwoDimGraphProps): React.ReactElement => {
    const echartRef = useRef(null);
    const setSettings = useCallback(() => {
        if (isChartViewedOnDashboard) return;
        setMapSettings(settings, echartRef.current, applyChartSettings);
    }, [settings, echartRef, applyChartSettings, isChartViewedOnDashboard]);

    const {
        mapLatCenter,
        mapLonCenter,
        mapZoom,
        showLegend,
        showLegendOnRightSide,
        isDataZoomEnabled,
        decimalPrecision,
        displayBy,
    } = settings;

    const { point: mapCenter, zoom: initMapZoom } = useMemo(() => {
        if (mapLatCenter && mapLonCenter && mapZoom) {
            return {
                point: { lat: mapLatCenter, lon: mapLonCenter },
                zoom: mapZoom,
            };
        }
        if (orderedColumns.x.type === 'coordinates') {
            return getGmapOptionsFromCoordinate(data, orderedColumns);
        }
        if (orderedColumns.x.type === 'polygon') {
            return getGmapOptionsFromPolygon(data, orderedColumns);
        }
        return {
            point: { lat: ZERO_MARK_FRANCE_LAT, lon: ZERO_MARK_FRANCE_LON },
            zoom: DEFAULT_MAP_ZOOM,
        };
    }, [data, orderedColumns, mapLatCenter, mapLonCenter, mapZoom]);

    const isGoogleMapsLoaded = useLoadGoogleMaps();
    if (!isGoogleMapsLoaded) {
        return <Loading />;
    }

    let series: GMapSerieT[] = [];
    const xColumn = orderedColumns.x;
    const yColumn = orderedColumns.y;
    const xKey = xColumn.key;
    const yKey = yColumn.key;
    const yType = yColumn.type;

    const filteredData = data.filter(
        (d) => d[xKey] !== UNKNOWN_DATUM && d[yKey] !== UNKNOWN_DATUM,
    );
    // Flatten data to handle the multi polygons
    const [flatData, flatDataRootIndexes] = flattenPolygonData(
        filteredData,
        xKey,
        yKey,
    );

    const maxY = _.max(data.map((d) => d[yKey]));
    const minY = _.min(data.map((d) => d[yKey]));
    // If all the values are the same, we set the max size to 1000 to lower their size and make the map clearer
    const MAX_SIZE = maxY === minY ? 1000 : 5000;

    const legendOptions = showLegendOnRightSide
        ? {
              right: 90 + getLegendWidth(width),
              left: 20,
              top: 20,
              bottom: 40,
          }
        : {
              right: 20,
              left: 20,
              top: showLegend ? 40 : 20,
              bottom: isDataZoomEnabled ? 70 : 40,
          };

    const emptySerie = {
        ...legendOptions,
        type: 'scatter',
        coordinateSystem: 'gmap',
        symbolSize(val: number[]) {
            if (typeof maxY !== 'number') {
                return NaN;
            }
            return Math.sqrt((MAX_SIZE * val[2]) / maxY);
        },
    };

    const colors = getColorsScale(settings?.palette);
    const zColumn = orderedColumns.z;

    const isPolygon = xColumn.type === 'polygon';

    if (isPolygon) {
        series = [
            {
                ...emptySerie,
                id: yKey,
                name: yColumn.label,
                type: 'custom',
                coordinateSystem: 'gmap',
                renderItem: (
                    params: echarts.CustomSeriesRenderItemParams,
                    api: echarts.CustomSeriesRenderItemAPI,
                ) => {
                    const points: number[][] = [];
                    const polygons: string[][] = flatData[params.dataIndex][
                        xKey
                    ] as string[][];
                    polygons.forEach((polygon) => {
                        polygon.forEach((coords) => {
                            points.push(api.coord(coords));
                        });
                    });
                    const color = api.visual('color');

                    return {
                        type: 'polygon',
                        shape: {
                            // TODO: We need to fix the import of echarts to have the correct type
                            // eslint-disable-next-line import-x/namespace
                            points: echarts.graphic.clipPointsByRect(points, {
                                // @ts-expect-error [Echart] x not defined in type Coordsys, there might be a bug here
                                x: params.coordSys.x,
                                // @ts-expect-error [Echart] y not defined in type Coordsys, there might be a bug here
                                y: params.coordSys.y,
                                // @ts-expect-error [Echart] width not defined in type Coordsys, there might be a bug here
                                width: params.coordSys.width,
                                // @ts-expect-error [Echart] height not defined in type Coordsys, there might be a bug here
                                height: params.coordSys.height,
                            }),
                        },
                        style: api.style({
                            fill: color,
                            stroke: '#F5F5F5',
                        }),
                    };
                },
                itemStyle: {
                    normal: {
                        opacity: 0.5,
                    },
                },
                data: flatData.map((d) => d[yKey] as GMapData),
            },
        ];
    } else if (xColumn.type === 'coordinates') {
        // The format of a data point for a map is [lon, lat, yValue]. We concatenate
        // the xValue (which is an 'lon-lat' string) with the yValue
        if (zColumn) {
            const zKey = zColumn.key;
            const groupedData = _.groupBy(data, (d) => d[zKey]);
            Object.entries(groupedData).forEach(([zValue, serieData]) => {
                series.push({
                    id: zValue,
                    name: formatDisplay(
                        zValue,
                        zColumn.type,
                        decimalPrecision,
                        displayBy,
                    ),

                    data: serieData.map((d) => ({
                        value: concatDataPointCoordinates(d, xKey, yKey),
                        // TODO: Use an explicit name for places
                        name: zValue,
                    })),
                    ...emptySerie,
                });
            });
        } else {
            series = [
                {
                    id: yKey,
                    name: yColumn.label,
                    data: data.map((d) => ({
                        value: concatDataPointCoordinates(d, xKey, yKey),
                    })),
                    ...emptySerie,
                },
            ];
        }
    }

    const zoom = settings.mapZoom || initMapZoom;

    const visualMap = isPolygon
        ? {
              min: minY,
              max: maxY,
              calculable: true,
              orient: 'horizontal',
              top: 'top',
              left: 'center',
              inRange: {
                  color: getContrastedColors(colors),
              },
              formatter: (value: number) =>
                  formatDisplay(value, yType, decimalPrecision, displayBy),
          }
        : null;

    const option = {
        animation: false,
        color: colors,
        legend: {
            show: isPolygon ? false : showLegend,
            itemWidth: 14,
            type: 'scroll',
            formatter(name: string) {
                return capitalizeAndKeepTheRest(name);
            },
        },
        tooltip: {
            trigger: 'item',
            formatter(param: {
                seriesName: string;
                seriesId: string;
                dataIndex: number;
                value: number[];
            }) {
                const { seriesName, seriesId, value } = param;
                let yValue;
                if (seriesId === 'citiesLimit') {
                    return value;
                }
                if (isPolygon) {
                    yValue = value;
                } else {
                    // values format is [lon, lat, yValue]
                    yValue = value[2];
                }
                const yString = `${capitalize(yColumn.label)} : ${formatDisplay(
                    yValue,
                    yType,
                    decimalPrecision,
                    displayBy,
                )}`;

                if (!zColumn) {
                    return yString;
                }

                const zValue = isPolygon
                    ? filteredData[flatDataRootIndexes[param.dataIndex]][
                          zColumn.key
                      ]
                    : seriesName;

                const zString = `${capitalize(zColumn.label)} : ${formatDisplay(
                    zValue,
                    zColumn.type,
                    decimalPrecision,
                    displayBy,
                )}`;

                return `${yString}<br>${zString}`;
            },
        },
        gmap: {
            center: [mapCenter.lon, mapCenter.lat],
            zoom,
        },
        visualMap,
        series,
    };
    return (
        <ReactEcharts
            onEvents={{
                globalout: setSettings,
            }}
            ref={echartRef}
            option={option}
            style={{ height, width }}
            className="map-chart"
        />
    );
};

export default React.memo(MapChart);
